/**
 * iter316 — Season-long play: box leagues and challenge ladders.
 * Both read from ordinary Court Mode matches, so there's no separate scoring.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Trophy, Loader2, Plus, ChevronRight, ArrowUp, ArrowDown, Swords, Check, X, Trash2,
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { firstName } from '../../lib/play';

const money = (n) => `\u20b9${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

export default function LeaguesCard({ groupId, members = [], userId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', type: 'box', box_size: 4, challenge_range: 3, entry_fee: '', member_ids: [] });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/groups/${groupId}/leagues`);
      setData(r.data);
    } catch {
      setData({ leagues: [], is_admin: false });
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  const create = () => {
    if (draft.member_ids.length < 4) return toast.error('Pick at least 4 players');
    return act(() => api.post(`/groups/${groupId}/leagues`, {
      ...draft,
      entry_fee: Number(draft.entry_fee) || 0,
      box_size: Number(draft.box_size) || 4,
      challenge_range: Number(draft.challenge_range) || 3,
    }), 'League started').then(() => setCreating(false));
  };

  if (!data) return null;
  const active = data.leagues.filter((l) => l.status === 'active');

  return (
    <div className="rounded-2xl border bg-card p-4 space-y-3" data-testid="leagues-card">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display text-sm font-bold uppercase tracking-[0.16em] inline-flex items-center gap-1.5">
          <Trophy className="w-4 h-4 text-amber-500" /> Leagues
        </h3>
        {data.is_admin && !creating && (
          <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs" onClick={() => setCreating(true)}
            data-testid="league-new-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New
          </Button>
        )}
      </div>

      {creating && (
        <div className="rounded-xl border p-3 space-y-2.5" data-testid="league-form">
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-muted p-1">
            {[['box', 'Box league'], ['ladder', 'Challenge ladder']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setDraft({ ...draft, type: k })}
                data-testid={`league-type-${k}`}
                className={`h-8 rounded-lg text-xs font-bold transition-colors ${draft.type === k ? 'bg-background shadow' : 'text-muted-foreground'}`}>
                {l}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {draft.type === 'box'
              ? 'Boxes of similar-level players. Any match between members of the same box counts for that box this round — the winner goes up, the bottom comes down.'
              : 'One ranked list. Challenge someone a few places above you; the next match where you two are on opposing teams settles it. Decline and you concede the place.'}
          </p>
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder={draft.type === 'box' ? 'Winter box league' : 'Club ladder'}
            className="h-10 rounded-xl border-2" data-testid="league-name-input" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide">
                {draft.type === 'box' ? 'Box size' : 'Challenge range'}
              </Label>
              <Input type="number" min="1" max="6"
                value={draft.type === 'box' ? draft.box_size : draft.challenge_range}
                onChange={(e) => setDraft({ ...draft, [draft.type === 'box' ? 'box_size' : 'challenge_range']: e.target.value })}
                className="h-10 rounded-xl border-2 mt-1" data-testid="league-size-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide">Entry fee</Label>
              <Input type="number" min="0" value={draft.entry_fee} placeholder="0"
                onChange={(e) => setDraft({ ...draft, entry_fee: e.target.value })}
                className="h-10 rounded-xl border-2 mt-1" data-testid="league-fee-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide">
              Players ({draft.member_ids.length})
            </Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {members.map((m) => {
                const on = draft.member_ids.includes(m.id);
                return (
                  <button key={m.id} type="button" data-testid={`league-member-${m.id}`}
                    onClick={() => setDraft({
                      ...draft,
                      member_ids: on ? draft.member_ids.filter((x) => x !== m.id) : [...draft.member_ids, m.id],
                    })}
                    className={`h-8 px-2.5 rounded-full border-2 text-xs font-semibold transition-colors ${
                      on ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                    }`}>
                    {firstName(m.name)}
                  </button>
                );
              })}
            </div>
          </div>
          {Number(draft.entry_fee) > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {money(draft.entry_fee)} will be charged to each of the {draft.member_ids.length} players
              ({money(Number(draft.entry_fee) * draft.member_ids.length)} into the fund).
            </p>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full flex-1 border-2 h-10" onClick={() => setCreating(false)}
              data-testid="league-cancel-btn">Cancel</Button>
            <Button className="rounded-full flex-1 h-10" disabled={busy} onClick={create} data-testid="league-create-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start league'}
            </Button>
          </div>
        </div>
      )}

      {!active.length && !creating && (
        <p className="text-xs text-muted-foreground" data-testid="leagues-empty">
          No league running. A box league keeps weekly games competitive; a ladder lets people chase the player above them.
        </p>
      )}

      {active.map((lg) => (
        <div key={lg.id} className="rounded-xl border p-3 space-y-2.5" data-testid={`league-${lg.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">{lg.name}</p>
              <p className="text-xs text-muted-foreground">
                {lg.type === 'box'
                  ? `Round ${lg.round} · ${lg.boxes_detail?.length || 0} boxes · ${lg.round_matches || 0} match${lg.round_matches === 1 ? '' : 'es'} this round`
                  : `${lg.ladder_detail?.length || 0} players · challenge up to ${lg.challenge_range} place${lg.challenge_range > 1 ? 's' : ''} above`}
                {lg.entry_fee > 0 ? ` · ${money(lg.entry_fee)} entry` : ''}
              </p>
            </div>
            {data.is_admin && (
              <div className="flex gap-1 shrink-0">
                {lg.type === 'box' && (
                  <Button size="sm" className="rounded-full h-8 text-xs" disabled={busy}
                    onClick={() => act(() => api.post(`/leagues/${lg.id}/advance`), 'Round closed — boxes reshuffled')}
                    data-testid={`league-advance-${lg.id}`}>
                    End round <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                  </Button>
                )}
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" disabled={busy}
                  onClick={() => window.confirm('Delete this league? Any entry fees are refunded.')
                    && act(() => api.delete(`/leagues/${lg.id}`), 'League deleted, fees refunded')}
                  data-testid={`league-delete-${lg.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                </Button>
              </div>
            )}
          </div>

          {lg.type === 'box' && (lg.boxes_detail || []).map((box) => (
            <div key={box.index} data-testid={`box-${lg.id}-${box.index}`}>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground mb-1">
                {box.label}
              </p>
              <div className="space-y-1">
                {box.table.map((r, i) => (
                  <div key={r.user_id} data-testid={`box-row-${r.user_id}`}
                    className="flex items-center gap-2 h-8 px-2.5 rounded-lg bg-muted/40 text-xs font-semibold">
                    <span className="w-4 text-center text-muted-foreground tabular-nums">{i + 1}</span>
                    <span className="flex-1 truncate">{firstName(r.name)}</span>
                    {i === 0 && box.index > 0 && box.matches > 0 && <ArrowUp className="w-3.5 h-3.5 text-emerald-600" />}
                    {i === box.table.length - 1 && box.index < (lg.boxes_detail.length - 1) && box.matches > 0
                      && <ArrowDown className="w-3.5 h-3.5 text-rose-500" />}
                    <span className="text-muted-foreground tabular-nums">{r.wins}W · {r.points}pts</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {lg.type === 'ladder' && (
            <>
              <div className="space-y-1">
                {(lg.ladder_detail || []).map((r) => (
                  <div key={r.user_id} data-testid={`ladder-row-${r.user_id}`}
                    className={`flex items-center gap-2 h-8 px-2.5 rounded-lg text-xs font-semibold ${
                      r.user_id === userId ? 'bg-primary/10 border border-primary/30' : 'bg-muted/40'
                    }`}>
                    <span className="w-4 text-center text-muted-foreground tabular-nums">{r.position}</span>
                    <span className="flex-1 truncate">{firstName(r.name)}</span>
                  </div>
                ))}
              </div>

              {!!(lg.my_targets || []).length && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground mb-1.5">
                    You can challenge
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {lg.my_targets.map((t) => (
                      <Button key={t.user_id} size="sm" variant="outline" className="rounded-full h-8 text-xs border-2"
                        disabled={busy} data-testid={`challenge-${t.user_id}`}
                        onClick={() => act(() => api.post(`/leagues/${lg.id}/challenges`, { opponent_id: t.user_id }),
                          `Challenge sent to ${firstName(t.name)}`)}>
                        <Swords className="w-3.5 h-3.5 mr-1" /> #{t.position} {firstName(t.name)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {(lg.challenges || []).map((c) => (
                <div key={c.id} className="rounded-xl border border-dashed p-2.5 space-y-1.5" data-testid={`challenge-row-${c.id}`}>
                  <p className="text-xs font-semibold">
                    {firstName(c.challenger_name)} challenged {firstName(c.opponent_name)}
                    <span className="text-muted-foreground font-normal"> · {c.status}</span>
                  </p>
                  {c.status === 'pending' && c.opponent_id !== userId && data.is_admin && (
                    <p className="text-xs text-muted-foreground">
                      Answering on {firstName(c.opponent_name)}'s behalf as admin.
                    </p>
                  )}
                  {c.status === 'pending' && (c.opponent_id === userId || data.is_admin) && (
                    <div className="flex gap-1.5">
                      <Button size="sm" className="rounded-full h-7 text-xs flex-1" disabled={busy}
                        onClick={() => act(() => api.post(`/challenges/${c.id}/respond`, { accept: true }), 'Challenge accepted')}
                        data-testid={`challenge-accept-${c.id}`}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Accept
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full h-7 text-xs flex-1 border-2" disabled={busy}
                        onClick={() => window.confirm('Declining gives up your place. Sure?')
                          && act(() => api.post(`/challenges/${c.id}/respond`, { accept: false }), 'Place conceded')}
                        data-testid={`challenge-decline-${c.id}`}>
                        <X className="w-3.5 h-3.5 mr-1" /> Decline
                      </Button>
                    </div>
                  )}
                  {c.status === 'accepted' && (
                    <Button size="sm" variant="outline" className="rounded-full h-7 text-xs w-full border-2" disabled={busy}
                      onClick={() => act(() => api.post(`/challenges/${c.id}/settle`), 'Ladder updated')}
                      data-testid={`challenge-settle-${c.id}`}>
                      Settle from our last match
                    </Button>
                  )}
                </div>
              ))}
            </>
          )}

          {lg.type === 'box' && !!(lg.history || []).length && (
            <p className="text-xs text-muted-foreground" data-testid={`league-history-${lg.id}`}>
              Last round: {(lg.history[lg.history.length - 1].moves || [])
                .map((m) => `${m.box} won by ${firstName(m.winner)}`).join(' · ') || 'no matches'}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
